import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { MeasureMetadata, WorkPeriod } from '../../api/work-periods-client/work-periods-client.type';
import { EntityType, Measure } from '../../api/work-periods-client/work-periods-client.type';
import type { FloraContextObject, FloraContextualInfo } from '../../containers/flora/flora.type';
import { FloraSupportedObjectTypes } from '../../containers/flora/flora.type';
import { Tab } from '../../containers/process-analysis/process-analysis.type';
import { GeneralViewNames } from '../../containers/side-bar/side-bar.type';

/**
 * Gets the entity type based on the active tab
 *
 * @param {Tab | string} activeTab - The active tab
 * @returns {EntityType} The entity type
 */
function getEntityType(activeTab: Tab | string): EntityType {
  const entityTypes: Record<string, EntityType> = {
    [Tab.Portfolios]: EntityType.Portfolio,
    [Tab.Teams]: EntityType.Project,
    [Tab.Boards]: EntityType.Subproject,
    work_periods: EntityType.Subproject,
    [Tab.Agents]: EntityType.Portfolio,
  };
  return entityTypes[activeTab] ?? EntityType.Portfolio;
}

/**
 * Gets the time allocation end date
 *
 * @param {string|undefined} startDate - The start date
 * @param {string|undefined} endDate - The end date
 * @returns {string} The time allocation end date
 */
const getTimeAllocationEndDate = (startDate: string | undefined, endDate: string | undefined): string => {
  if (!startDate || !endDate) {
    return '';
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return '';
  }

  const hoursDiff = end.diff(start, 'hours');

  if (hoursDiff >= 23 && hoursDiff <= 25) {
    return start.format('YYYY-MM-DD');
  }

  const diffInDays = end.diff(start, 'day');

  if (diffInDays === 1) {
    return start.format('YYYY-MM-DD');
  }

  return end.format('YYYY-MM-DD');
};

/**
 * Splits the measures into groups
 *
 * @param {Measure[]} measures - The measures
 * @param {number} numGroups - The number of groups
 * @returns {Measure[][]} The groups
 */
const splitMeasures = (measures: Measure[], numGroups = 2): Measure[][] => {
  if (numGroups <= 0) {
    return [];
  }

  const groupSize = Math.ceil(measures.length / numGroups);
  const groups: Measure[][] = [];

  for (let i = 0; i < numGroups; i++) {
    const start = i * groupSize;
    const end = Math.min(start + groupSize, measures.length);
    groups.push(measures.slice(start, end));
  }

  return groups;
};

/**
 * Gets the last non-null value from a time series data object
 *
 * @param {Record<string, number | null | undefined> | undefined | null} timeSeriesData - The time series data object
 * @returns {number | null} The last non-null value
 */
const getLastNotNullValue = (
  timeSeriesData: Record<string, number | null | undefined> | undefined | null,
): number | null => {
  if (!timeSeriesData) return null;
  const dates = Object.keys(timeSeriesData).sort();
  for (let i = dates.length - 1; i >= 0; i--) {
    const value = timeSeriesData[dates[i]];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return null;
};

/**
 * Gets the sprint comparison flora context
 *
 * @param {WorkPeriod[]} workPeriods - The work periods
 * @returns {FloraContextualInfo} The sprint comparison flora context
 */
const getSprintComparisonFloraContext = (workPeriods: WorkPeriod[]): FloraContextualInfo => {
  const objects: FloraContextObject[] = [];
  const minStartDate =
    workPeriods.length > 0
      ? workPeriods.reduce(
          (min, wp) => (dayjs(wp.start_date).isBefore(dayjs(min)) ? wp.start_date : min),
          workPeriods[0].start_date,
        )
      : null;
  const maxEndDate =
    workPeriods.length > 0
      ? workPeriods.reduce(
          (max, wp) => (dayjs(wp.end_date).isAfter(dayjs(max)) ? wp.end_date : max),
          workPeriods[0].end_date,
        )
      : null;
  for (const workPeriod of workPeriods) {
    objects.push({
      type: FloraSupportedObjectTypes.WorkPeriod,
      id: workPeriod.id,
      name: workPeriod.name,
    });
  }
  return {
    currentModule: GeneralViewNames.WorkPeriods,
    currentObjects: objects,
    startDate: minStartDate,
    endDate: maxEndDate,
  };
};

/**
 * Interpolates the measure metadata
 *
 * @param {MeasureMetadata[]} measures - The measure metadata
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {MeasureMetadata[]} The interpolated measure metadata
 */
const interpolateMeasureMetadata = (measures: MeasureMetadata[], t: TFunction): MeasureMetadata[] => {
  const workUnitsPlural = t('common.work_units');
  const workUnitSingular = t('common.work_unit');
  const estimationPlural = t('common.estimation');
  const estimationSingular = t('common.estimation_singular');

  const interpolateText = (text: string): string => {
    if (!text) return text;

    return text
      .replace(/\bTasks\b/g, workUnitsPlural)
      .replace(/\btasks\b/g, workUnitsPlural.toLowerCase())
      .replace(/\bPoints\b/g, estimationPlural)
      .replace(/\bpoints\b/g, estimationPlural.toLowerCase())
      .replace(/\bTask\b/g, workUnitSingular)
      .replace(/\btask\b/g, workUnitSingular.toLowerCase())
      .replace(/\bPoint\b/g, estimationSingular)
      .replace(/\bpoint\b/g, estimationSingular.toLowerCase());
  };

  return measures.map((measure) => ({
    ...measure,
    measure_title: interpolateText(measure.measure_title),
    measure_description: interpolateText(measure.measure_description),
  }));
};

export {
  getEntityType,
  getLastNotNullValue,
  getSprintComparisonFloraContext,
  getTimeAllocationEndDate,
  interpolateMeasureMetadata,
  splitMeasures,
};
